import type {
  Channel,
  InboxConversationPage,
  InboxConversationSummary,
  InboxTag,
} from '@sbc/domain-models/gateway';
import { INBOX_CATEGORIES, INBOX_STATUSES } from '@sbc/domain-models/gateway';
import { AtSign, ChevronDown, MessageCircle, Search } from 'lucide-react';
import type { RefObject } from 'react';

import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import {
  DropdownMenu,
  DropdownMenuCheckboxItem,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { Input } from '@/components/ui/input';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { cn, pick } from '@/lib/utils';

import { InboxAvatar } from './inbox-avatar';
import {
  CATEGORY_LABELS,
  CHANNEL_GROUP_LABELS,
  CHANNEL_NAMES,
  STATUS_LABELS,
  SURFACE_LABELS,
  channelFilterLabel,
  channelFilterOptions,
  channelGroupOptions,
  displayName,
  groupSelection,
  ownerLabel,
  relativeTime,
  closedRepeatsLabel,
  toggleChannel,
  toggleGroup,
} from './inbox-format';
import { ALL, DEFAULT_STATUS, INBOX_TABS, type InboxSearch, TAB_LABELS } from './inbox-search';
import { visibleTags } from './inbox-tags';

export function ConversationRow({
  conversation,
  selected,
  now,
  onSelect,
}: {
  conversation: InboxConversationSummary;
  selected: boolean;
  now: Date;
  onSelect: (id: string) => void;
}) {
  const name = displayName(conversation);
  const unread = conversation.unread === true;
  const SurfaceIcon = conversation.surface === 'dm' ? MessageCircle : AtSign;
  return (
    <li>
      <button
        type="button"
        data-conversation-id={conversation.id}
        aria-current={selected ? 'true' : undefined}
        onClick={() => onSelect(conversation.id)}
        className={cn(
          'flex w-full items-start gap-3 border-b px-3 py-3 text-left transition-colors hover:bg-[var(--color-accent)] focus-visible:bg-[var(--color-accent)] focus-visible:outline-none',
          selected && 'bg-[var(--color-accent)]',
        )}
      >
        <InboxAvatar name={name} channel={conversation.channel} />
        <span className="min-w-0 flex-1">
          <span className="flex items-center gap-2">
            <span className={cn('min-w-0 flex-1 truncate text-sm', unread && 'font-bold')}>
              {name}
            </span>
            <span className="shrink-0 text-xs text-[var(--color-muted-foreground)]">
              {relativeTime(conversation.lastInboundAt, now)}
            </span>
          </span>
          <span className="mt-0.5 flex items-center gap-2">
            <span
              className={cn(
                'min-w-0 flex-1 truncate text-sm',
                unread
                  ? 'font-semibold text-[var(--color-foreground)]'
                  : 'text-[var(--color-muted-foreground)]',
              )}
            >
              {conversation.lastMessageText || 'No messages'}
            </span>
            {unread && (
              <span className="size-2.5 shrink-0 rounded-full bg-sky-500">
                <span className="sr-only">Unread</span>
              </span>
            )}
          </span>
          <span className="mt-1 flex items-center gap-2 text-xs text-[var(--color-muted-foreground)]">
            <span className="inline-flex shrink-0 items-center gap-1 rounded border px-1.5 py-px">
              <SurfaceIcon className="size-3" />
              {SURFACE_LABELS[conversation.surface]}
            </span>
            {conversation.category === 'order_issue' && (
              <span className="shrink-0 font-medium text-[var(--color-destructive)]">
                {CATEGORY_LABELS.order_issue}
              </span>
            )}
            {conversation.closedRepeatCount > 0 && (
              <span className="shrink-0">{closedRepeatsLabel(conversation.closedRepeatCount)}</span>
            )}
            {conversation.status !== DEFAULT_STATUS && (
              <span className="shrink-0">{STATUS_LABELS[conversation.status]}</span>
            )}
            <span
              className={cn(
                'ml-auto min-w-0 truncate',
                conversation.assignee?.isViewer && 'font-medium text-[var(--color-foreground)]',
              )}
            >
              {ownerLabel(conversation.assignee)}
            </span>
          </span>
          {conversation.tags.length > 0 && <RowTags tags={conversation.tags} />}
        </span>
      </button>
    </li>
  );
}

const ROW_TAGS = 2;

function RowTags({ tags }: { tags: readonly InboxTag[] }) {
  const { shown, more } = visibleTags(tags, ROW_TAGS);
  return (
    <span aria-label="Tags" className="mt-1 flex min-w-0 items-center gap-1">
      {shown.map((tag) => (
        <Badge key={tag.id} variant="secondary" className="max-w-32 truncate px-1.5 py-0">
          {tag.name}
        </Badge>
      ))}
      {more > 0 && (
        <span className="shrink-0 text-xs text-[var(--color-muted-foreground)]">+{more}</span>
      )}
    </span>
  );
}

function keepMenuOpen(event: Event) {
  event.preventDefault();
}

function ChannelFilter({
  selected,
  connected,
  onChange,
}: {
  selected: readonly Channel[];
  connected: readonly Channel[];
  onChange: (channels: Channel[]) => void;
}) {
  const groups = channelGroupOptions(connected);
  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button
          variant="outline"
          aria-label="Channel"
          className="h-8 w-full justify-between px-3 text-xs font-normal"
        >
          <span className="truncate">{channelFilterLabel(selected, groups)}</span>
          <ChevronDown className="size-4 opacity-50" />
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="start" className="w-56">
        <DropdownMenuItem onSelect={() => onChange([])} disabled={selected.length === 0}>
          All channels
        </DropdownMenuItem>
        {groups.length > 0 && (
          <>
            <DropdownMenuSeparator />
            <DropdownMenuLabel className="text-xs text-[var(--color-muted-foreground)]">
              Groups
            </DropdownMenuLabel>
            {groups.map((group) => (
              <DropdownMenuCheckboxItem
                key={group.id}
                checked={groupSelection(group, selected)}
                onSelect={keepMenuOpen}
                onCheckedChange={() => onChange(toggleGroup(group, selected))}
              >
                {CHANNEL_GROUP_LABELS[group.id]}
              </DropdownMenuCheckboxItem>
            ))}
          </>
        )}
        <DropdownMenuSeparator />
        <DropdownMenuLabel className="text-xs text-[var(--color-muted-foreground)]">
          Channels
        </DropdownMenuLabel>
        {channelFilterOptions(connected, selected).map((channel) => (
          <DropdownMenuCheckboxItem
            key={channel}
            checked={selected.includes(channel)}
            onSelect={keepMenuOpen}
            onCheckedChange={() => onChange(toggleChannel(channel, selected))}
          >
            {CHANNEL_NAMES[channel]}
          </DropdownMenuCheckboxItem>
        ))}
      </DropdownMenuContent>
    </DropdownMenu>
  );
}

export function ConversationList({
  search,
  page,
  channels,
  tags,
  loading,
  error,
  selectedId,
  searchInputRef,
  onSearchChange,
  onSelect,
}: {
  search: InboxSearch;
  page: InboxConversationPage | undefined;
  channels: readonly Channel[];
  tags: readonly InboxTag[];
  loading: boolean;
  error: Error | null;
  selectedId: string | undefined;
  searchInputRef: RefObject<HTMLInputElement | null>;
  onSearchChange: (patch: Partial<InboxSearch>, options?: { replace?: boolean }) => void;
  onSelect: (id: string) => void;
}) {
  const tab = search.tab ?? 'all';
  const status = search.status ?? DEFAULT_STATUS;
  const currentPage = search.page ?? 1;
  const pageCount = page ? Math.max(1, Math.ceil(page.total / page.pageSize)) : 1;
  const now = new Date();

  return (
    <div className="flex h-full min-h-0 flex-col">
      <div className="space-y-3 border-b p-3">
        <div className="relative">
          <Search className="pointer-events-none absolute top-1/2 left-2.5 size-4 -translate-y-1/2 text-[var(--color-muted-foreground)]" />
          <Input
            ref={searchInputRef}
            placeholder="Search name or message  ( / )"
            aria-label="Search conversations"
            value={search.q ?? ''}
            onChange={(event) =>
              onSearchChange(
                { q: event.target.value === '' ? undefined : event.target.value },
                { replace: true },
              )
            }
            onKeyDown={(event) => {
              if (event.key === 'Escape') {
                event.currentTarget.blur();
              }
            }}
            className="h-9 pl-8"
          />
        </div>
        <div aria-label="Conversation filter" className="flex flex-wrap gap-0.5">
          {INBOX_TABS.map((value) => (
            <Button
              key={value}
              aria-pressed={tab === value}
              size="sm"
              variant={tab === value ? 'secondary' : 'ghost'}
              className="h-7 px-2 text-xs"
              onClick={() => onSearchChange({ tab: value })}
            >
              {TAB_LABELS[value]}
            </Button>
          ))}
        </div>
        <div className="grid grid-cols-2 gap-2">
          <ChannelFilter
            selected={search.channel ?? []}
            connected={channels}
            onChange={(channel) => onSearchChange({ channel })}
          />
          <Select
            value={status}
            onValueChange={(value) =>
              onSearchChange({ status: value === ALL ? ALL : pick(INBOX_STATUSES, value) })
            }
          >
            <SelectTrigger className="h-8 w-full text-xs" aria-label="Status">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {INBOX_STATUSES.map((value) => (
                <SelectItem key={value} value={value}>
                  {STATUS_LABELS[value]}
                </SelectItem>
              ))}
              <SelectItem value={ALL}>Any status</SelectItem>
            </SelectContent>
          </Select>
          <Select
            value={search.category ?? ALL}
            onValueChange={(value) => onSearchChange({ category: pick(INBOX_CATEGORIES, value) })}
          >
            <SelectTrigger className="h-8 w-full text-xs" aria-label="Category">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value={ALL}>Topics</SelectItem>
              {INBOX_CATEGORIES.map((value) => (
                <SelectItem key={value} value={value}>
                  {CATEGORY_LABELS[value]}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          <Select
            value={search.tag ?? ALL}
            onValueChange={(value) => onSearchChange({ tag: value === ALL ? undefined : value })}
          >
            <SelectTrigger className="h-8 w-full text-xs" aria-label="Tag">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value={ALL}>Tags</SelectItem>
              {tags.map((tag) => (
                <SelectItem key={tag.id} value={tag.id}>
                  {tag.name}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      </div>

      <div className="relative min-h-0 flex-1 overflow-y-auto">
        {loading && !page && (
          <p className="p-4 text-sm text-[var(--color-muted-foreground)]">Loading…</p>
        )}
        {error && (
          <p className="p-4 text-sm text-[var(--color-destructive)]">
            Failed to load the inbox: {error.message}
          </p>
        )}
        {page &&
          (page.items.length ? (
            <ul aria-label="Conversations">
              {page.items.map((conversation) => (
                <ConversationRow
                  key={conversation.id}
                  conversation={conversation}
                  selected={conversation.id === selectedId}
                  now={now}
                  onSelect={onSelect}
                />
              ))}
            </ul>
          ) : (
            <p className="p-6 text-center text-sm text-[var(--color-muted-foreground)]">
              Nothing here.
            </p>
          ))}
      </div>

      {page && (
        <div className="flex items-center justify-between gap-2 border-t px-3 py-2 text-xs text-[var(--color-muted-foreground)]">
          <span>{page.total} conversation(s)</span>
          {pageCount > 1 && (
            <span className="flex items-center gap-1">
              <Button
                variant="ghost"
                size="sm"
                className="h-7 px-2"
                disabled={currentPage <= 1}
                onClick={() => onSearchChange({ page: currentPage - 1 })}
              >
                Previous
              </Button>
              <span>
                {currentPage} / {pageCount}
              </span>
              <Button
                variant="ghost"
                size="sm"
                className="h-7 px-2"
                disabled={currentPage >= pageCount}
                onClick={() => onSearchChange({ page: currentPage + 1 })}
              >
                Next
              </Button>
            </span>
          )}
        </div>
      )}
    </div>
  );
}
